import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';

import css from './MainPanelHeader.module.css';
import { useLiveCount } from '../../../util/liveResultCount';

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} props.noResultsInfo - The no results info
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
    availableNowOnly = false,
    onSelectAnyTime,
    onSelectAvailableNow,
    onToggleAvailableNow,
    onToggleAvailableNowOnly,
  } = props;
  const liveCount = useLiveCount(resultsCount);

  const classes = classNames(rootClassName || css.root, className);
  const selectAnyTime = onSelectAnyTime || onToggleAvailableNow;
  const selectAvailableNow = onSelectAvailableNow || onToggleAvailableNowOnly;
  const hasAvailabilityControls = selectAnyTime || selectAvailableNow;
  const anyTimeSelected = !availableNowOnly;
  const availableNowControl = hasAvailabilityControls ? (
    <div className={css.availabilityControls} role="group">
      {selectAnyTime ? (
        <button
          type="button"
          className={classNames(css.availableNowToggle, {
            [css.availableNowToggleOn]: anyTimeSelected,
          })}
          aria-pressed={anyTimeSelected}
          onClick={selectAnyTime}
        >
          <FormattedMessage id="MainPanelHeader.anyTime" />
        </button>
      ) : null}
      {selectAvailableNow ? (
        <button
          type="button"
          className={classNames(css.availableNowToggle, {
            [css.availableNowToggleOn]: availableNowOnly,
          })}
          aria-pressed={availableNowOnly}
          onClick={selectAvailableNow}
        >
          <FormattedMessage id="MainPanelHeader.availableNow" />
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        <div className={css.titleBlock}>
          <h1 className={availableNowOnly ? css.availableNowTitle : css.searchResultSummary}>
            <span className={css.resultsFound}>
              {searchInProgress ? (
                <FormattedMessage id="MainPanelHeader.loadingResults" />
              ) : availableNowOnly ? (
                <FormattedMessage id="MainPanelHeader.availableToTalkNow" />
              ) : (
                <FormattedMessage
                  id="MainPanelHeader.foundResults"
                  values={{ count: liveCount }}
                />
              )}
            </span>
          </h1>
          {availableNowOnly && listingsAreLoaded && !searchInProgress ? (
            <p className={css.readyToListen}>
              <FormattedMessage
                id="MainPanelHeader.readyToListen"
                values={{ count: liveCount }}
              />
            </p>
          ) : null}
          {availableNowOnly && selectAnyTime ? (
            <p className={css.seeAllWrap}>
              <button type="button" className={css.seeAllListeners} onClick={selectAnyTime}>
                <FormattedMessage id="MainPanelHeader.seeAllListeners" />
              </button>
            </p>
          ) : null}
        </div>
        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            {availableNowControl}
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : availableNowControl ? (
          <div className={css.sortyByWrapper}>{availableNowControl}</div>
        ) : null}
      </div>
      {hasAvailabilityControls && !availableNowOnly ? (
        <p className={css.availabilityHint}>
          <FormattedMessage id="MainPanelHeader.availableNowFirstHint" />
        </p>
      ) : null}
      <p className={css.notTherapy}>
        <FormattedMessage id="SearchPage.notTherapy" />
      </p>

      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

export default MainPanelHeader;
